import { defineNuxtPlugin } from "nuxt/app";
import type { $Fetch, FetchRequest } from "ofetch";
import { useTracking } from "@composables/useTracking";
import { SearchType } from '@domain/enums/SearchType';

type TrackingFetchOptions = {
	baseURL?: string;
	_trackingStart?: number;
	_trackingDone?: boolean;
	_trackingSearchType?: SearchType;
};

type GlobalFetch = typeof globalThis extends { $fetch: infer F } ? F : $Fetch;

export default defineNuxtPlugin((nuxtApp) => {
	if (!import.meta.client) return;

	const nuxtAppWithFetch = nuxtApp as unknown as { $fetch: GlobalFetch };
	const baseFetch: GlobalFetch = globalFetch.$fetch ?? nuxtAppWithFetch.$fetch;

	const timedFetch = baseFetch.create({
		onRequest: ({ request, options }) => {
			const trackingOptions = options as TrackingFetchOptions;
			const searchType = getTrackedSearchType(request);
			if (!searchType) return;

			trackingOptions._trackingStart = now();
			trackingOptions._trackingSearchType = searchType;
		},

		onResponse: ({ options }) => {
			trackResponseDuration(options as TrackingFetchOptions);
		},

		onResponseError: ({ options }) => {
			trackResponseDuration(options as TrackingFetchOptions);
		}
	});

	globalFetch.$fetch = timedFetch;
	nuxtAppWithFetch.$fetch = timedFetch;
});


const now = (): number => (typeof performance === "undefined" ? Date.now() : performance.now());
const tracking = useTracking();
const globalFetch = globalThis as typeof globalThis & { $fetch?: GlobalFetch };

const toRequestUrl = (request: FetchRequest): string | null => {
	if (typeof request === "string") return request;

	if (request instanceof URL) return request.toString();

	return request.url;
};

const getTrackedSearchType = (request: FetchRequest): SearchType | null => {
	const requestUrl = toRequestUrl(request);
	if (!requestUrl) return null;

	if (requestUrl.includes('/api/search/programmes')) return SearchType.PROGRAMME_SEARCH;
	if (requestUrl.includes('/api/search/organisations')) return SearchType.UNIVERSITY_SEARCH;
	if (requestUrl.includes('/api/search/scholarships')) return SearchType.SCHOLARSHIP_SEARCH;

	return null;
};

const trackResponseDuration = (options: TrackingFetchOptions): void => {
	if (options._trackingDone) return;

	const start = options._trackingStart;
	if (typeof start !== "number") return;
	const searchType = options._trackingSearchType;
	if (!searchType) return;

	options._trackingDone = true;
	const duration = Math.max(0, Math.round(now() - start));
	tracking.trackAPIResponseTime(searchType, duration);
};
